import { useMemo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { getTuning } from '../game/generation/config/tuning';
import { bandForLevel } from '../game/generation/director';
import { CURRENT_CONTENT_VERSION } from '../game/model/identity';
import { DIFFICULTY_BAND_LABELS } from '../game/model/recipe';
import { formatDuration } from '../state/activeTimer';
import { useAppStore } from '../state/store';
import { BackIcon, Button, IconButton } from '../ui/components';

const PAGE_SIZE = 60;

/**
 * Level selection.
 *
 * The campaign is generated, not authored, so the list is unbounded: pages are rendered on
 * demand and only levels up to the highest unlocked one are playable.
 */
export function LevelSelectScreen({
  onBack,
  onPlay,
}: {
  onBack(): void;
  onPlay(levelNumber: number): void;
}) {
  const { progress } = useAppStore(useShallow((state) => ({ progress: state.progress })));
  const [page, setPage] = useState(() =>
    Math.floor((progress.highestUnlockedLevel - 1) / PAGE_SIZE),
  );

  const tuning = getTuning(CURRENT_CONTENT_VERSION);
  const levels = useMemo(() => {
    const start = page * PAGE_SIZE + 1;
    return Array.from({ length: PAGE_SIZE }, (_, index) => start + index);
  }, [page]);

  return (
    <div className="ar-screen">
      <header
        className="ar-row"
        data-justify="between"
        style={{ marginBottom: 'var(--ar-space-4)' }}
      >
        <IconButton label="Back" onClick={onBack}>
          <BackIcon />
        </IconButton>
        <h1 className="ar-subtitle">Levels</h1>
        <span style={{ width: '2.75rem' }} />
      </header>

      <div
        className="ar-level-grid"
        style={{ margin: '0 auto', overflowY: 'auto', paddingBottom: 'var(--ar-space-4)' }}
      >
        {levels.map((levelNumber) => {
          const completion = progress.completions[levelNumber];
          const unlocked = levelNumber <= progress.highestUnlockedLevel;
          const state = completion !== undefined ? 'complete' : unlocked ? 'unlocked' : 'locked';
          const band = bandForLevel(levelNumber, tuning);

          return (
            <button
              key={levelNumber}
              type="button"
              className="ar-level-tile"
              data-state={state}
              disabled={!unlocked}
              onClick={() => onPlay(levelNumber)}
              aria-label={
                unlocked
                  ? `Level ${levelNumber}, ${DIFFICULTY_BAND_LABELS[band]}${
                      completion === undefined
                        ? ''
                        : `, cleared in ${formatDuration(completion.bestTimeMs)}`
                    }`
                  : `Level ${levelNumber}, locked`
              }
            >
              <span>{levelNumber}</span>
              <span className="ar-level-tile__meta">
                {completion !== undefined
                  ? formatDuration(completion.bestTimeMs)
                  : unlocked
                    ? DIFFICULTY_BAND_LABELS[band]
                    : 'Locked'}
              </span>
            </button>
          );
        })}
      </div>

      <div className="ar-row" style={{ justifyContent: 'center', marginTop: 'var(--ar-space-4)' }}>
        <Button onClick={() => setPage((value) => Math.max(0, value - 1))} disabled={page === 0}>
          Previous
        </Button>
        <span className="ar-muted">
          {page * PAGE_SIZE + 1}–{(page + 1) * PAGE_SIZE}
        </span>
        <Button
          onClick={() => setPage((value) => value + 1)}
          disabled={(page + 1) * PAGE_SIZE >= progress.highestUnlockedLevel + PAGE_SIZE}
        >
          Next
        </Button>
      </div>
    </div>
  );
}
